import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { certificateAPI } from '../../../certificates/api/certificateAPI';
import EmptyState from '../common/EmptyState';
import { withAppBasename } from '../../../../config/appBase';

const PROGRAMME_PRESETS = [
  'Medical Work Experience Week',
  'LNAT & Oxford Legal Reasoning Programme',
  'TMUA Advanced Mathematical Thinking',
  'Oxbridge Interview & Critical Reasoning Lab',
  'Quantitative Finance & Case Analysis Track',
];

const CertificatesTab = ({ students }) => {
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [programme, setProgramme] = useState(PROGRAMME_PRESETS[0]);
  const [customProgramme, setCustomProgramme] = useState('');
  const [duration, setDuration] = useState('35 hours (approximately)');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [skills, setSkills] = useState('Clinical Observation, Medical Ethics, Patient Care');
  const [issued, setIssued] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const debounceRef = useRef(null);

  // Both certificate endpoints are unscoped: search_registered_students and
  // list_all_certificates return every user and every certificate on the
  // platform to any authorised issuer. Until that is fixed server-side we
  // filter to the cohort actually in front of this teacher. This is a
  // mitigation, not a fix -- see the backend follow-up in the PR.
  const cohortEmails = useMemo(
    () => new Set((students || []).map((s) => String(s.email || '').toLowerCase()).filter(Boolean)),
    [students],
  );
  const cohortIds = useMemo(
    () => new Set((students || []).map((s) => String(s.id))),
    [students],
  );

  const inCohort = useCallback(
    (row) => cohortIds.has(String(row.user_id))
      || cohortEmails.has(String(row.email || row.student_email || '').toLowerCase()),
    [cohortIds, cohortEmails],
  );

  const loadCertificates = useCallback(async () => {
    try {
      const all = await certificateAPI.listCertificates(100);
      setCertificates((all || []).filter(inCohort));
    } catch (_) {
      setCertificates([]);
    }
  }, [inCohort]);

  useEffect(() => { loadCertificates(); }, [loadCertificates]);

  // Debounced: the old page fired a request on every keystroke past 2 chars.
  const onSearch = (value) => {
    setStudentName(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await certificateAPI.searchStudents(value);
        setSuggestions((results || []).filter(inCohort));
      } catch (_) {
        setSuggestions([]);
      }
    }, 250);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const issue = async (e) => {
    e.preventDefault();
    setError('');
    setIssued(null);
    const prog = programme === 'custom' ? customProgramme.trim() : programme;
    if (!studentName.trim()) { setError('Please provide the recipient student name.'); return; }
    if (!prog) { setError('Please specify the programme title.'); return; }
    try {
      setIssuing(true);
      const res = await certificateAPI.issueCertificate({
        student_name: studentName.trim(),
        student_email: studentEmail.trim() || undefined,
        programme_name: prog,
        issue_date: date,
        duration,
        skills: skills.split(',').map((s) => s.trim()).filter(Boolean),
      });
      if (res && res.certificate) {
        setIssued(res.certificate);
        loadCertificates();
      } else {
        setError('Failed to issue certificate.');
      }
    } catch (err) {
      setError(err.message || 'Error issuing certificate');
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="td-certs">
      <section className="td-card">
        <h3 className="td-card__title">Issue a certificate</h3>
        {error ? <div className="td-error">{error}</div> : null}
        {issued ? (
          <div className="td-notice">
            Issued <strong>{issued.id}</strong> to {issued.student_name}.{' '}
            <a href={withAppBasename(`/verify/${issued.id}`)} target="_blank" rel="noopener noreferrer">
              View verification page
            </a>
          </div>
        ) : null}

        <form className="td-certform" onSubmit={issue}>
          <label className="td-field td-field--autocomplete">
            <span>Student name</span>
            <input value={studentName} onChange={(e) => onSearch(e.target.value)} required />
            {suggestions.length > 0 ? (
              <ul className="td-autocomplete">
                {suggestions.map((s) => (
                  <li key={s.user_id}>
                    <button
                      type="button"
                      onClick={() => {
                        setStudentName(s.full_name || s.email);
                        setStudentEmail(s.email || '');
                        setSuggestions([]);
                      }}
                    >
                      {s.full_name || s.email}
                      <span>{s.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </label>

          <label className="td-field">
            <span>Student email</span>
            <input type="email" value={studentEmail} onChange={(e) => setStudentEmail(e.target.value)} />
          </label>

          <label className="td-field">
            <span>Programme</span>
            <select value={programme} onChange={(e) => setProgramme(e.target.value)}>
              {PROGRAMME_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
              <option value="custom">Custom…</option>
            </select>
          </label>

          {programme === 'custom' ? (
            <label className="td-field">
              <span>Custom programme title</span>
              <input value={customProgramme} onChange={(e) => setCustomProgramme(e.target.value)} required />
            </label>
          ) : null}

          <label className="td-field">
            <span>Duration</span>
            <input value={duration} onChange={(e) => setDuration(e.target.value)} />
          </label>

          <label className="td-field">
            <span>Issue date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>

          <label className="td-field td-field--wide">
            <span>Skills (comma separated)</span>
            <input value={skills} onChange={(e) => setSkills(e.target.value)} />
          </label>

          <button type="submit" className="td-btn td-btn--confirm" disabled={issuing}>
            {issuing ? 'Issuing…' : 'Issue certificate'}
          </button>
        </form>
      </section>

      <section className="td-card">
        <h3 className="td-card__title">Issued to this group</h3>
        {certificates.length === 0 ? (
          <EmptyState title="No certificates issued yet" body="Certificates issued to students in this group appear here." />
        ) : (
          <ul className="td-certlist">
            {certificates.map((c) => (
              <li key={c.id}>
                <span className="td-certlist__name">{c.student_name}</span>
                <span className="td-certlist__prog">{c.programme_name}</span>
                <span className="td-certlist__date">{c.issue_date}</span>
                <a href={withAppBasename(`/verify/${c.id}`)} target="_blank" rel="noopener noreferrer">Verify</a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default CertificatesTab;
